import { Button, Input, Modal, Segmented, Skeleton, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { AiOutlineArrowLeft, AiOutlineDownload, AiOutlineEdit, AiOutlineSearch } from "react-icons/ai";
import { BsBuildings, BsPersonBadge, BsPersonCheck, BsPersonX, BsPeople } from "react-icons/bs";
import { HiOutlineUsers } from "react-icons/hi";
import { Link, Navigate, useParams } from "react-router-dom";

import getPermissions from "../../utils/getPermissions";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import { getRoles } from "../role/roleApis";
import EditUserModal from "../user/EditUserModal";
import "../user/userList.css";
import { DepartmentForm } from "./AddDepartment";
import { getDepartments, loadSingleDepartment } from "./departmentApis";

const avatarColors = [
	"from-[#023f88] to-[#0b5cae]",
	"from-sky-500 to-blue-600",
	"from-indigo-500 to-violet-600",
	"from-amber-500 to-orange-500",
	"from-rose-500 to-pink-600",
];

const getInitials = (firstName, lastName) =>
	`${(firstName || "").charAt(0)}${(lastName || "").charAt(0)}`.toUpperCase() || "?";

const userTypeLabels = { 1: "Department Head", 2: "Coordinator" };

const StatCard = ({ icon: Icon, label, value, accent }) => (
	<div className='flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70'>
		<div
			className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}>
			<Icon className='text-2xl' />
		</div>
		<div>
			<p className='text-3xl font-extrabold leading-none text-slate-900'>{value}</p>
			<p className='mt-1 text-sm font-medium text-slate-600'>{label}</p>
		</div>
	</div>
);

const columns = (onEdit, canEdit) => [
	{
		title: "User",
		key: "fullName",
		sorter: (a, b) =>
			`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
		render: ({ id, firstName, lastName, email }) => (
			<div className='flex items-center gap-3'>
				<div
					className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${
						avatarColors[id % avatarColors.length]
					} text-sm font-bold text-white shadow`}>
					{getInitials(firstName, lastName)}
				</div>
				<div className='min-w-0'>
					<p className='truncate font-semibold text-slate-800'>
						{`${firstName || ""} ${lastName || ""}`.trim() || "Unnamed user"}
					</p>
					<p className='truncate text-sm text-slate-600'>{email}</p>
				</div>
			</div>
		),
	},
	{
		title: "Phone",
		dataIndex: "phone",
		key: "phone",
		render: (phone) => phone || <span className='text-slate-300'>—</span>,
	},
	{
		title: "Job Title",
		dataIndex: "jobTitle",
		key: "jobTitle",
		render: (jobTitle) => jobTitle || <span className='text-slate-300'>—</span>,
	},
	{
		title: "User Type",
		dataIndex: "usertype",
		key: "usertype",
		render: (usertype) => (
			<span
				className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
					Number(usertype) === 1
						? "bg-blue-100 text-[#023f88] ring-1 ring-blue-200"
						: "bg-violet-100 text-violet-800 ring-1 ring-violet-200"
				}`}>
				{userTypeLabels[usertype] || (usertype ? String(usertype) : "—")}
			</span>
		),
	},
	{
		title: "Status",
		dataIndex: "status",
		key: "status",
		render: (status) => (
			<span
				className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
					status
						? "bg-green-100 text-green-800 ring-1 ring-green-200"
						: "bg-red-100 text-red-700 ring-1 ring-red-200"
				}`}>
				<span className={`h-1.5 w-1.5 rounded-full ${status ? "bg-green-500" : "bg-red-500"}`} />
				{status ? "Active" : "Inactive"}
			</span>
		),
	},
	...(canEdit
		? [
				{
					title: "Action",
					key: "action",
					render: (record) => (
						<Button
							className='rounded-lg inline-flex items-center gap-1.5 border-[#023f88] text-[#023f88] font-semibold'
							icon={<AiOutlineEdit />}
							onClick={() => onEdit(record)}>
							Edit
						</Button>
					),
				},
		  ]
		: []),
];

const DetailDepartment = () => {
	const { id } = useParams();
	const permissions = getPermissions() || [];
	const canReadUsers = permissions.includes("readAll-customer");
	const canEditUser = permissions.includes("update-customer");
	const canRename = permissions.includes("update-department");

	const [department, setDepartment] = useState(null);
	const [users, setUsers] = useState(null);
	const [departments, setDepartments] = useState([]);
	const [roles, setRoles] = useState([]);
	const [editingUser, setEditingUser] = useState(null);
	const [renameOpen, setRenameOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("All");

	const loadUsers = () =>
		axios
			.get(`customer?query=all`)
			.then(({ data }) =>
				setUsers((data || []).filter((u) => Number(u.departmentId) === Number(id)))
			)
			.catch((error) => {
				console.log(error);
				setUsers([]);
			});

	useEffect(() => {
		loadSingleDepartment(id).then((d) => setDepartment(d?.data || null));
		if (canReadUsers) loadUsers();
		if (canEditUser) {
			getDepartments().then((d) => setDepartments(d || [])).catch(console.log);
			getRoles().then((r) => setRoles(r || [])).catch(console.log);
		}
		// eslint-disable-next-line
	}, [id]);

	const allUsers = useMemo(() => users || [], [users]);
	const activeCount = allUsers.filter((u) => u.status).length;
	const headCount = allUsers.filter((u) => Number(u.usertype) === 1).length;
	const coordinatorCount = allUsers.filter((u) => Number(u.usertype) === 2).length;

	const filteredUsers = useMemo(() => {
		const term = search.trim().toLowerCase();
		return allUsers
			.filter((u) => {
				if (statusFilter === "Active" && !u.status) return false;
				if (statusFilter === "Inactive" && u.status) return false;
				if (!term) return true;
				return [u.firstName, u.lastName, u.email, u.phone, u.jobTitle]
					.filter(Boolean)
					.some((v) => String(v).toLowerCase().includes(term));
			})
			.map((u) => ({ ...u, key: u.id }));
	}, [allUsers, search, statusFilter]);

	const isLogged = Boolean(localStorage.getItem("isLogged"));
	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	return (
		<UserPrivateComponent permission={"readSingle-department"}>
			<div className='max-w-7xl mx-auto py-6 md:py-8'>
				<Link
					to='/admin/setup/department'
					className='mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0b5cae] transition-colors'>
					<AiOutlineArrowLeft />
					All departments
				</Link>

				{/* Header */}
				<div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] px-6 py-8 md:px-10 text-white shadow-xl'>
					<div className='absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl' />
					<div className='absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-2xl' />

					{department ? (
						<div className='relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between'>
							<div className='flex items-center gap-5'>
								<div className='hidden sm:flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm shadow-lg'>
									<BsBuildings className='text-3xl' />
								</div>
								<div>
									<span className='inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide backdrop-blur-sm'>
										Department
									</span>
									<h1 className='mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-white'>
										{department.name}
									</h1>
									<p className='mt-1 text-blue-50/90'>
										Created {dayjs(department.createdAt).format("DD MMM YYYY")}
										{users && ` · ${allUsers.length} ${allUsers.length === 1 ? "user" : "users"}`}
									</p>
								</div>
							</div>

							<div className='flex flex-wrap gap-3'>
								{canRename && (
									<button
										type='button'
										onClick={() => setRenameOpen(true)}
										className='inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#023f88] shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl'>
										<AiOutlineEdit className='text-lg' />
										Rename
									</button>
								)}
								{allUsers.length > 0 && (
									<CSVLink
										data={allUsers}
										filename={`${department.name}-users`}
										className='inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/25 hover:text-white'>
										<AiOutlineDownload className='text-lg' />
										Download CSV
									</CSVLink>
								)}
							</div>
						</div>
					) : (
						<div className='relative'>
							<Skeleton active title={{ width: 240 }} paragraph={{ rows: 1 }} />
						</div>
					)}
				</div>

				{canReadUsers ? (
					<>
						{/* Stats */}
						<div className='mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5'>
							<StatCard icon={BsPeople} label='Total users' value={allUsers.length} accent='from-[#023f88] to-[#0b5cae]' />
							<StatCard icon={BsPersonCheck} label='Active' value={activeCount} accent='from-green-500 to-green-600' />
							<StatCard icon={BsPersonX} label='Inactive' value={allUsers.length - activeCount} accent='from-rose-500 to-red-600' />
							<StatCard icon={BsPersonBadge} label='Department Heads' value={headCount} accent='from-sky-500 to-blue-600' />
							<StatCard icon={HiOutlineUsers} label='Coordinators' value={coordinatorCount} accent='from-indigo-500 to-violet-600' />
						</div>

						{/* Users */}
						<div className='mt-6 rounded-2xl bg-white p-4 md:p-6 shadow-sm ring-1 ring-slate-200/70'>
							<div className='mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
								<div>
									<h2 className='text-xl font-bold text-slate-900'>Users in this department</h2>
									<p className='text-sm text-slate-600'>
										{filteredUsers.length} of {allUsers.length} users
									</p>
								</div>
								<div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
									<Input
										allowClear
										size='large'
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										prefix={<AiOutlineSearch className='text-slate-400' />}
										placeholder='Search users…'
										className='rounded-xl sm:w-72'
									/>
									<Segmented
										value={statusFilter}
										onChange={setStatusFilter}
										options={["All", "Active", "Inactive"]}
									/>
								</div>
							</div>

							<Table
								className='cp-users-table'
								columns={columns(setEditingUser, canEditUser)}
								dataSource={filteredUsers}
								loading={!users}
								rowKey='id'
								scroll={{ x: 800 }}
								pagination={{
									pageSize: 10,
									showSizeChanger: false,
									showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} users`,
								}}
								locale={{
									emptyText: (
										<div className='py-10 text-center'>
											<HiOutlineUsers className='mx-auto text-4xl text-slate-300' />
											<p className='mt-2 font-semibold text-slate-700'>
												{allUsers.length ? "No users match your search" : "No users in this department yet"}
											</p>
											{!allUsers.length && (
												<Link
													to='/customer/registerAdmin'
													className='mt-3 inline-block rounded-xl bg-gradient-to-r from-[#023f88] to-[#0b5cae] px-4 py-2 text-sm font-semibold text-white hover:text-white'>
													Add a user
												</Link>
											)}
										</div>
									),
								}}
							/>
						</div>
					</>
				) : (
					<div className='mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center'>
						<HiOutlineUsers className='mx-auto text-4xl text-slate-300' />
						<p className='mt-3 font-semibold text-slate-700'>You can't view users</p>
						<p className='mt-1 text-sm text-slate-600'>
							Ask an administrator for the permission to read users.
						</p>
					</div>
				)}
			</div>

			<EditUserModal
				user={editingUser}
				roles={roles}
				departments={departments}
				onClose={() => setEditingUser(null)}
				onSaved={() => {
					setEditingUser(null);
					loadUsers();
				}}
			/>

			<Modal
				open={renameOpen}
				onCancel={() => setRenameOpen(false)}
				footer={null}
				centered
				destroyOnClose
				title={
					<div className='flex items-center gap-3'>
						<div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#023f88] to-[#0b5cae] text-white'>
							<AiOutlineEdit />
						</div>
						<div>
							<p className='text-lg font-bold text-slate-900'>Rename department</p>
							<p className='text-sm font-normal text-slate-600'>{department?.name}</p>
						</div>
					</div>
				}>
				<div className='pt-2'>
					<DepartmentForm
						department={department}
						onSaved={(saved) => {
							if (saved) setDepartment((prev) => ({ ...prev, ...saved }));
							setRenameOpen(false);
						}}
					/>
				</div>
			</Modal>
		</UserPrivateComponent>
	);
};

export default DetailDepartment;
